import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import { getTexts } from "../../api/text";
import { postAnswer } from "../../api/answer";
import handGood from "../../assets/hand-good.png";
import logoSupertest from "../../assets/logo-supertest-blue.png";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import { getTimer, updateStats } from "../../utils/helpers";
import { alertError } from "../../utils/logger";
import { mapQuizToQuestion } from "./mapQuizToQuestion";

const SCENARIO_ID = SCENARIOS.SUPERTEST;
const NAVIGATE_TO = ROUTES.SUPERTEST_END;

const CORRECT_ANSWER_POINTS = 50;

/** Devuelve 1, 2 o 3 según el número de pregunta. Busca primero en resultExplanation ("Pregunta 1:", etc.), luego en fullName/screenTitle. Si no coincide, 999. */
function getSupertestQuestionOrderFromQuiz(quizData) {
	if (!quizData) return 999;
	const expl = (quizData.resultExplanation != null ? String(quizData.resultExplanation) : "").trim();
	const pregMatch = expl.match(/Pregunta\s*(\d)/i) || expl.match(/^(\d)[.:]\s/);
	if (pregMatch) return Math.min(Number(pregMatch[1]), 3) || 999;
	const name = (quizData.fullName != null ? String(quizData.fullName) : "") + " " + (quizData.screenTitle != null ? String(quizData.screenTitle) : "");
	const numMatch = name.match(/\b(1|2|3)\b/);
	return numMatch ? Number(numMatch[1]) : 999;
}

const DEFAULT_QUESTION_OPTIONS = {
	1: {
		quizId: QUIZZES.SUPERTEST_1,
		title: "¿Cuál de las siguientes afirmaciones NO es correcta?",
		options: [
			{ id: "a)", text: "La NL constituye la afectación orgánica grave más frecuente del LES, dado que hasta el 60 % de los pacientes con LES pueden llegar a tener NL durante el curso de su vida.", selected: false, correct: false },
			{ id: "b)", text: "La NL se caracteriza por la aparición de proteinuria en grado variable, alteraciones en el sedimento urinario, deterioro de la función renal y en algunos casos, desarrollo de hipertensión arterial.", selected: false, correct: false },
			{ id: "c)", text: "La presencia de NL se asocia con un mayor riesgo de complicaciones cardiovasculares, daño orgánico irreversible y mortalidad prematura.", selected: false, correct: false },
			{ id: "d)", text: "Son incorrectas las afirmaciones B y C.", selected: false, correct: false },
			{ id: "e)", text: "No hay afirmaciones incorrectas.", selected: false, correct: true },
		],
	},
	2: {
		quizId: QUIZZES.SUPERTEST_2,
		title: "¿Cuál de las siguientes afirmaciones acerca de voclosporina es correcta?",
		options: [
			{ id: "a)", text: "Ha demostrado ser eficaz en nefritis lúpica de nuevo diagnóstico.", selected: false, correct: false },
			{ id: "b)", text: "Ha demostrado ser eficaz en recaídas.", selected: false, correct: false },
			{ id: "c)", text: "Se puede utilizar en pacientes con nefritis lúpica de clase IV y V, pero no III.", selected: false, correct: false },
			{ id: "d)", text: "A y B son correctas.", selected: false, correct: true },
			{ id: "e)", text: "A, B y C son correctas.", selected: false, correct: false },
		],
	},
	3: {
		quizId: QUIZZES.SUPERTEST_3,
		title: "Voclosporina, como parte del tratamiento estándar de NL durante 3 años, tiene un perfil riesgo-beneficio positivo. ¿Cuál de las siguientes afirmaciones es correcta en relación con la afirmación anterior?",
		options: [
			{ id: "a)", text: "La preservación de la función renal a largo plazo, junto con los resultados de seguridad favorables del estudio Aurora 2, dan a entender que esta afirmación es verdadera.", selected: false, correct: true },
			{ id: "b)", text: "Aunque la voclosporina puede ofrecer beneficios terapéuticos en el tratamiento de la NL, su uso como parte del tratamiento estándar durante 3 años aún plantea interrogantes sobre su tolerabilidad, por lo que esta afirmación, tal y como está formulada, es falsa.", selected: false, correct: false },
			{ id: "c)", text: "Si bien los resultados de seguridad en el estudio Aurora 1 fueron positivos, los del estudio Aurora 2 desaconsejan el uso de voclosporina durante un período mayor de un año, por lo que la afirmación es falsa.", selected: false, correct: false },
		],
	},
};

function Supertest({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const participant = useSelector((state) => state.group);

	const [time, setTime] = useState(0);
	const [isCompleted, setIsCompleted] = useState(false);
	const [screenTitle, setScreenTitle] = useState("");
	const [legend, setLegend] = useState("");
	const [questionOptions, setQuestionOptions] = useState(DEFAULT_QUESTION_OPTIONS);
	const [currentQuestionNum, setCurrentQuestionNum] = useState(1);
	const [resultModalTexts, setResultModalTexts] = useState({
		titleSuccess: "¡Enhorabuena! ¡Has acertado todas las respuestas!",
		titleFail: "¡Vaya! Has fallado al menos una de las respuestas…",
		subtitleSuccess: "+50 puntos",
		subtitleFail: "He aquí las justificaciones",
		references: "",
		justificationTitle: "",
		justificationContent: "",
	});
	const [resultModalOpen, setResultModalOpen] = useState(false);
	const [resultModalData, setResultModalData] = useState(null);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const gameId = participant?.game;
	useEffect(() => {
		if (!gameId) return;
		getTexts({ game: gameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					const map = res.data.reduce((acc, t) => ({ ...acc, [t.key]: t.value }), {});
					if (map["supertest.title"] != null && map["supertest.title"] !== "") setScreenTitle(map["supertest.title"]);
					if (map["supertest.content"] != null && map["supertest.content"] !== "") setLegend(map["supertest.content"]);
					setResultModalTexts((prev) => ({
						...prev,
						...(map["supertest.result.titleSuccess"] != null && map["supertest.result.titleSuccess"] !== "" && { titleSuccess: map["supertest.result.titleSuccess"] }),
						...(map["supertest.result.titleFail"] != null && map["supertest.result.titleFail"] !== "" && { titleFail: map["supertest.result.titleFail"] }),
						...(map["supertest.result.subtitleSuccess"] != null && map["supertest.result.subtitleSuccess"] !== "" && { subtitleSuccess: map["supertest.result.subtitleSuccess"] }),
						...(map["supertest.result.subtitleFail"] != null && map["supertest.result.subtitleFail"] !== "" && { subtitleFail: map["supertest.result.subtitleFail"] }),
						...(map["supertest.result.references"] != null && { references: map["supertest.result.references"] }),
						...(map["supertest.justification.title"] != null && { justificationTitle: map["supertest.justification.title"] }),
						...(map["supertest.justification.content"] != null && { justificationContent: map["supertest.justification.content"] }),
					}));
				}
			})
			.catch(() => {});
	}, [gameId]);

	const fetchSupertestQuizzes = React.useCallback(() => {
		const cacheBuster = { _t: Date.now(), ...(gameId ? { game: gameId } : {}) };
		const fallbackIds = [QUIZZES.SUPERTEST_1, QUIZZES.SUPERTEST_2, QUIZZES.SUPERTEST_3];
		const fetchByIds = (ids) =>
			Promise.all(ids.map((id) => getQuizById(id, cacheBuster))).then((responses) => ({ responses, quizIds: ids }));
		getQuizIdsByScenario(SCENARIO_ID, cacheBuster)
			.then((listRes) => {
				const list = listRes?.status === 200 && Array.isArray(listRes?.data) ? listRes.data : [];
				const quizIds = list.length >= 3 ? [list[0]._id, list[1]._id, list[2]._id] : fallbackIds;
				return fetchByIds(quizIds);
			})
			.catch(() => fetchByIds(fallbackIds))
			.then(({ responses, quizIds }) => {
				const pairs = responses.map((res, i) => ({ res, quizId: quizIds[i] }));
				const sorted = [...pairs].sort((a, b) => getSupertestQuestionOrderFromQuiz(a.res?.data) - getSupertestQuestionOrderFromQuiz(b.res?.data));
				const fromApi = {};
				sorted.forEach(({ res, quizId }, index) => {
					const num = index + 1;
					const raw = res?.data;
					if (res?.status === 200 && raw && typeof raw === "object" && Array.isArray(raw?.options) && raw.options.length > 0) {
						const q = mapQuizToQuestion(raw, quizId);
						if (q) fromApi[num] = q;
					}
				});
				if (Object.keys(fromApi).length > 0) {
					setQuestionOptions(() => ({ ...DEFAULT_QUESTION_OPTIONS, ...fromApi }));
				}
			})
			.catch(() => {});
	}, [gameId]);

	useEffect(() => {
		fetchSupertestQuizzes();
		const onVisible = () => {
			if (document.visibilityState === "visible") fetchSupertestQuizzes();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => document.removeEventListener("visibilitychange", onVisible);
	}, [fetchSupertestQuizzes]);


	const SelectOption = ({ id }) => {
		let option = questionOptions[currentQuestionNum]?.options?.find(
			(x) => x.id === id
		);

		function getBackgroundColor() {
			return option?.selected && option?.correct && isCompleted
				? "#D6D431"
				: option?.selected && !option?.correct && isCompleted
				? "#FF5948"
				: option?.selected
				? "#EAF886"
				: "#FFFFFF";
		}

		function handleOptionClick() {
			if (isCompleted) return;

			let updatedOptions = questionOptions[
				currentQuestionNum
			]?.options?.map((x) =>
				x.id === id
					? {
							...x,
							selected: !x.selected,
					  }
					: { ...x, selected: false }
			);

			setQuestionOptions((prevOptions) => ({
				...prevOptions,
				[currentQuestionNum]: {
					...prevOptions[currentQuestionNum],
					options: updatedOptions,
				},
			}));
		}

		return (
			<div>
				<div
					className={`d-flex align-items-center justify-content-start txt-dark-green mt-3 p-3 ${
						option?.selected ? "option-selected bold" : ""
					}`}
					style={{
						height: "fit-content",
						lineHeight: "20px",
						borderRadius: "10px",
						backgroundColor: getBackgroundColor(),
					}}
					onClick={handleOptionClick}
				>
					<div className="d-flex align-items-start justify-content-start">
						<div className="bold">{option?.id}</div>
						<div className="ms-1" dangerouslySetInnerHTML={{ __html: option?.text || "" }} />
					</div>
				</div>
			</div>
		);
	};

	function checkAnswers() {
		if (
			!questionOptions[currentQuestionNum]?.options?.some(
				(x) => x?.selected
			)
		) {
			alertError({
				error: " ",
				customMessage: "Para avanzar, debes seleccionar una opción.",
			});
			return;
		}

		if (currentQuestionNum === Object.keys(questionOptions)?.length) {
			const correct = Object.keys(questionOptions).every((key) =>
				questionOptions[key]?.options?.some(
					(x) => x?.selected && x?.correct
				)
			);
			const gameId = participant?.game;
			const defaultRef = "<sup>1</sup> Rojas-Rivera JE, Praga Terente M. Nefropatía Lúpica. Lupus Eritematoso Sistémico. En: Lorenzo V, López Gómez JM (eds). Nefrología al día. Disponible en: https://www.nefrologiaaldia.org/589 [última consulta: 5 marzo 2024].<br/><sup>2</sup> Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80. <br/><sup>3</sup> Saxena A, Ginzler EM, Gibson K, et al. Safety and efficacy of long-Term voclosporin treatment for lupus nephritis in the phase 3 AURORA 2 clinical trial. Arthritis Rheumatol. 2024;76(1):59-67.<br/><sup>4</sup> Arriens C, Onno Teng YK, Ginzler EM, et al. Update on the efficacy and safety profile of voclosporin: an integrated analysis of clinical trials in lupus nephritis. Arthritis Care Res (Hoboken). 2023;75(7):1399-1408.<br/><strong>NL:</strong> nefritis lúpica";
			const defaultExpl1 = "todas las afirmaciones son correctas. Cabe destacar que la NL se caracteriza por la aparición de proteinuria en grado variable (40-100 %)<sup>1</sup>, alteraciones en el sedimento urinario (hematuria hasta en el 80 %), deterioro de la función renal (40-80%) y en algunos casos, desarrollo de hipertensión arterial (15-50 %)<sup>1</sup>. Además, el control adecuado de tensión arterial y de la dislipemia, frecuente en los pacientes con NL, son importantes para prevenir eventos cardiovasculares<sup>1</sup>.";
			const defaultExpl2 = "voclosporina ha demostrado ser eficaz en nefritis lúpica de nuevo diagnóstico y en recaídas y en varios perfiles de pacientes, incluidos pacientes con NL de clase III<sup>2-4</sup>.";
			const defaultExpl3 = "la afirmación es verdadera: la preservación de la función renal a largo plazo, junto con los resultados de seguridad favorables del estudio Aurora 2, establecen un perfil beneficio-riesgo positivo para voclosporina como parte del tratamiento estándar de NL durante 3 años<sup>3</sup>.";
			const buildModalData = (t, expl1, expl2, expl3) => {
				const title = correct ? t.titleSuccess : t.titleFail;
				const subtitleHtml = correct
					? `<div class="bg-correct p-2 w-fit-content"><img width="25" src=${handGood} class="me-1" /><span class="bold">${t.subtitleSuccess}</span></div>`
					: t.subtitleFail;
				// Referencias: solo desde Textos supertest.result.references (no Justificación content)
				const refContent = (t.references != null && String(t.references).trim() !== "") ? t.references : defaultRef;
				const refBlock = `<div class="txt9 mt-4">${refContent}</div>`;
				// Siempre cuerpo desde las 3 justificaciones de cada quiz (resultExplanation), no desde Textos Justificación
				const bodyHtml = `<div class="text-left"><div class="mt-3">${expl1}</div><div class="mt-2">${expl2}</div><div class="mt-2">${expl3}</div>${refBlock}</div>`;
				return { title, subtitleHtml, bodyHtml, correct };
			};
			const fetchThreeQuizzes = () => {
				const cacheBuster = { _t: Date.now(), ...(gameId ? { game: gameId } : {}) };
				const fallbackIds = [QUIZZES.SUPERTEST_1, QUIZZES.SUPERTEST_2, QUIZZES.SUPERTEST_3];
				return getQuizIdsByScenario(SCENARIO_ID, cacheBuster)
					.then((listRes) => {
						const list = listRes?.status === 200 && Array.isArray(listRes?.data) ? listRes.data : [];
						const ids = list.length >= 3 ? [list[0]._id, list[1]._id, list[2]._id] : fallbackIds;
						return Promise.all(ids.map((id) => getQuizById(id, cacheBuster)));
					})
					.catch(() => Promise.all(fallbackIds.map((id) => getQuizById(id, { _t: Date.now() }))));
			};
			const openModal = (modalData) => {
				setResultModalData(modalData);
				setResultModalOpen(true);
			};
			const textsPromise = gameId ? getTexts({ game: gameId }) : Promise.resolve({ status: 0 });
			Promise.all([textsPromise, fetchThreeQuizzes()])
				.then(([textRes, quizResponses]) => {
					const map = (textRes?.status === 200 && Array.isArray(textRes?.data))
						? textRes.data.reduce((acc, item) => ({ ...acc, [item.key]: item.value }), {})
						: {};
					const t = {
						...resultModalTexts,
						titleSuccess: map["supertest.result.titleSuccess"] ?? resultModalTexts.titleSuccess,
						titleFail: map["supertest.result.titleFail"] ?? resultModalTexts.titleFail,
						subtitleSuccess: map["supertest.result.subtitleSuccess"] ?? resultModalTexts.subtitleSuccess,
						subtitleFail: map["supertest.result.subtitleFail"] ?? resultModalTexts.subtitleFail,
						references: map["supertest.result.references"] !== undefined ? (map["supertest.result.references"] ?? "") : resultModalTexts.references,
						justificationTitle: map["supertest.justification.title"] !== undefined ? (map["supertest.justification.title"] ?? "") : resultModalTexts.justificationTitle,
						justificationContent: map["supertest.justification.content"] !== undefined ? (map["supertest.justification.content"] ?? "") : resultModalTexts.justificationContent,
					};
					if (Object.keys(map).length > 0) setResultModalTexts(t);
					const sortedResponses = [...quizResponses].sort((a, b) => getSupertestQuestionOrderFromQuiz(a?.data) - getSupertestQuestionOrderFromQuiz(b?.data));
					const getExpl = (res, def) => {
						const raw = res?.data;
						const expl = (raw?.resultExplanation != null && String(raw.resultExplanation).trim() !== "") ? String(raw.resultExplanation).trim() : null;
						return expl || def;
					};
					const expl1 = getExpl(sortedResponses[0], defaultExpl1);
					const expl2 = getExpl(sortedResponses[1], defaultExpl2);
					const expl3 = getExpl(sortedResponses[2], defaultExpl3);
					openModal(buildModalData(t, expl1, expl2, expl3));
				})
				.catch(() => {
					const t = resultModalTexts;
					const expl1 = (questionOptions[1]?.resultExplanation ?? "").trim() || defaultExpl1;
					const expl2 = (questionOptions[2]?.resultExplanation ?? "").trim() || defaultExpl2;
					const expl3 = (questionOptions[3]?.resultExplanation ?? "").trim() || defaultExpl3;
					openModal(buildModalData(t, expl1, expl2, expl3));
				});
		} else setCurrentQuestionNum(currentQuestionNum + 1);
	}

	async function submitAnswer({ correct }) {
		setIsCompleted(true);

		const score = correct ? CORRECT_ANSWER_POINTS : 0;

		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: questionOptions[currentQuestionNum]?.quizId,
			group: participant?._id,
			score: score,
			time: time,
			correct: correct,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(score, SCORE_TYPES.SCORE));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + score,
						lastVisited: NAVIGATE_TO,
					});
					navigate(NAVIGATE_TO);
				}
			})
			.catch((error) => {});
	}

	const currentQuestion = questionOptions[currentQuestionNum];
	// Enunciado: usar título del quiz (screenTitle desde API); si solo viene el nombre corto, usar default
	const rawTitle = currentQuestion?.title || screenTitle;
	const isShortName = rawTitle === "Supertest 1" || rawTitle === "Supertest 2" || rawTitle === "Supertest 3";
	const displayTitle = isShortName ? (DEFAULT_QUESTION_OPTIONS[currentQuestionNum]?.title ?? rawTitle) : rawTitle;
	const displayLegend = (currentQuestion?.legend != null && String(currentQuestion.legend).trim() !== "") ? currentQuestion.legend : legend;

	return (
		<div className="h-100 bg-red">
			<div className="container">
				<div className="row h-100">
					<div className="row d-flex justify-content-center align-items-start mt-4">
						<div className="col-2">
							<img width={70} src={logoSupertest} />
						</div>
						<div className="col-8">
							<div
								className="d-flex justify-content-center txtWhite txt20 bold mb-4"
								dangerouslySetInnerHTML={{ __html: displayTitle }}
							/>
							{(currentQuestion?.options || []).map((option) => (
								<div key={option?.id}>
									<SelectOption id={option.id} />
								</div>
							))}
							<br />
							<div
								className="txtWhite"
								dangerouslySetInnerHTML={{ __html: displayLegend }}
							/>
						</div>
						<div className="col-2"></div>
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "4vh",
					bottom: "10vh",
					zIndex: 1000,
				}}
			>
	<div className="d-flex justify-content-end">
					{!isCompleted && currentQuestion && !resultModalOpen && (
						<div
							className="btn-main-cancel-white"
							onClick={() => checkAnswers()}
						>
							<div className="text-btn-main">AVANZAR</div>
						</div>
					)}
				</div>
			</div>
			{resultModalOpen && resultModalData && (
				<div
					className="position-fixed d-flex align-items-center justify-content-center"
					style={{
						inset: 0,
						zIndex: 9999,
						backgroundColor: "rgba(0,0,0,0.5)",
					}}
					onClick={(e) => e.target === e.currentTarget && (setResultModalOpen(false), setResultModalData(null))}
				>
					<div
						className="bg-white rounded p-4 mx-3 shadow"
						style={{ maxWidth: "90%", maxHeight: "90vh", overflow: "auto" }}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="txt14 txt-black supertest-result-modal-body">
							<div className="text-center bold txt-turquoise txt18" dangerouslySetInnerHTML={{ __html: resultModalData.title }} />
							<div className="d-flex justify-content-center mt-3" dangerouslySetInnerHTML={{ __html: resultModalData.subtitleHtml }} />
							<div className="mt-3" dangerouslySetInnerHTML={{ __html: resultModalData.bodyHtml }} />
						</div>
						<div className="d-flex justify-content-end mt-4">
							<button
								type="button"
								className="btn-main"
								onClick={() => {
									submitAnswer({ correct: resultModalData.correct });
									setResultModalOpen(false);
									setResultModalData(null);
								}}
							>
								AVANZAR
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default injectIntl(Supertest);
